import { displaySettlementLineName } from "@/utils/payroll-names";
import { useLocale } from "@/hooks/useLocale";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const fmt = (n: number) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const DocField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[8px] uppercase tracking-widest text-gray-400 font-semibold leading-none mb-0.5">
      {label}
    </span>
    <span className="text-[10px] font-semibold text-gray-800 leading-tight">
      {value}
    </span>
  </div>
);

const MIN_TABLE_ROWS = 5;

export type SettlementDocLine = {
  name: string;
  amount: number;
};

export type SettlementDocStatutoryRow = {
  label: string;
  basis: string;
  rate: number;
  employeeAmt: number;
  employerAmt: number;
};

/** The employer the document is issued by (B17) — never the software vendor. */
export type SettlementDocCompany = {
  name: string;
  logo?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
};

export type SettlementDocProps = {
  refNo: string;
  company?: SettlementDocCompany | null;
  issueDate: string;
  employee: {
    name: string;
    employeeId: string;
    designation?: string | null;
    department?: string | null;
    joiningDate?: string | null;
    lastWorkingDay?: string | null;
  };
  payment: {
    method?: string | null;
    payDate?: string | null;
    bankName?: string | null;
    accountNumber?: string | null;
  };
  currency: string;
  earnings: SettlementDocLine[];
  deductions: SettlementDocLine[];
  totalEarnings: number;
  totalDeductions: number;
  statutoryRows?: SettlementDocStatutoryRow[];
  totalStatutoryEmployee?: number;
  totalStatutoryEmployer?: number;
  netSalary: number;
  sticky?: boolean;
};

/** Month names follow the language (B53): dayjs had no locale set. */
const formatHumanDate = (
  formatDate: (d: Date, pattern: string) => string,
  iso?: string | null,
  longMonth = false,
): string => {
  if (!iso) return "—";
  const d = dayjs(iso);
  if (!d.isValid()) return "—";
  return formatDate(d.toDate(), longMonth ? "dd MMMM yyyy" : "dd/MM/yyyy");
};

const titleCase = (s?: string | null) =>
  s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

export const SettlementDocument = ({
  refNo,
  company,
  issueDate,
  employee,
  payment,
  currency,
  earnings,
  deductions,
  totalEarnings,
  totalDeductions,
  statutoryRows = [],
  totalStatutoryEmployee = 0,
  totalStatutoryEmployer = 0,
  netSalary,
  sticky = false,
}: SettlementDocProps) => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate } = useLocale();
  const ccy = currency || "AED";
  const rowCount = Math.max(earnings.length, deductions.length, MIN_TABLE_ROWS);
  const earningsRows = [
    ...earnings,
    ...Array(rowCount - earnings.length).fill(null),
  ];
  const deductionRows = [
    ...deductions,
    ...Array(rowCount - deductions.length).fill(null),
  ];

  return (
    <div
      className={`bg-white rounded-sxs border border-gray-200 shadow-md overflow-hidden text-[10px] leading-relaxed select-none ${
        sticky ? "sticky top-4" : ""
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3">
        {company?.logo ? (
          <img
            src={company.logo}
            alt={company.name}
            className="h-8 w-auto max-w-[140px] object-contain"
          />
        ) : (
          <span />
        )}
        <p className="text-[9px] font-black tracking-[0.18em] text-gray-600 uppercase text-end">
          {/* One title: two keys read "التسوية النهائية الكاملة / التسوية" (B53). */}
          {t("company:payrolls.settlement_document_title")}
        </p>
      </div>

      <div className="h-0.5 bg-primary" />

      {/* Company + Payslip Details */}
      <div className="grid grid-cols-2 gap-4 px-5 py-3">
        <div>
          <p className="text-[8px] font-bold uppercase tracking-widest text-primary mb-1.5">
            {t("company:payrolls.company")}
          </p>
          <p className="text-[11px] font-bold text-gray-800">
            {company?.name || "—"}
          </p>
          {company?.address && (
            <p className="text-[9px] text-gray-500 leading-snug">
              {company.address}
            </p>
          )}
          {(company?.email || company?.phone) && (
            <p className="text-[9px] text-gray-500 mt-0.5">
              {[company?.email, company?.phone].filter(Boolean).join(" | ")}
            </p>
          )}
        </div>
        <div className="text-end">
          <p className="text-[8px] font-bold uppercase tracking-widest text-primary mb-1.5">
            {t("company:payrolls.payslip_details")}
          </p>
          <div className="flex flex-col gap-0.5 text-[9px]">
            <p className="text-gray-600">
              {t("company:payrolls.issue_date")}:{" "}
              <span className="font-semibold text-gray-800">{issueDate}</span>
            </p>
            <p className="text-gray-600">
              {t("company:payrolls.ref_no")}{" "}
              <span className="font-bold text-gray-800">{refNo}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Employee Information */}
      <div className="px-5 pb-2">
        <div className="border-b-2 border-primary pb-0.5 mb-2">
          <p className="text-[9px] font-bold uppercase tracking-widest text-primary">
            {t("company:payrolls.employee_information")}
          </p>
        </div>
        <div className="grid grid-cols-4 gap-x-3 gap-y-2">
          <DocField
            label={t("company:payrolls.employee_name")}
            value={employee.name || "—"}
          />
          <DocField
            label={t("company:payrolls.employee_id")}
            value={employee.employeeId || "—"}
          />
          <DocField
            label={t("common:labels.designation")}
            value={employee.designation || "—"}
          />
          <DocField
            label={t("common:labels.department")}
            value={employee.department || "—"}
          />
          <DocField
            label={t("company:payrolls.joining_date")}
            value={formatHumanDate(formatDate, employee.joiningDate)}
          />
          <DocField
            label={t("company:payrolls.last_working_day")}
            value={formatHumanDate(formatDate, employee.lastWorkingDay, true)}
          />
        </div>
      </div>

      {/* Payment Details */}
      <div className="px-5 pb-2">
        <div className="border-b border-gray-100 pb-0.5 mb-2">
          <p className="text-[9px] font-bold uppercase tracking-widest text-primary">
            {t("company:payrolls.payment_details")}
          </p>
        </div>
        <div className="grid grid-cols-4 gap-x-3">
          <DocField
            label={t("company:payrolls.payment_method")}
            value={
              payment.method
                ? t(`common:payment_methods.${payment.method.toLowerCase()}`, {
                    defaultValue: titleCase(payment.method),
                  })
                : ""
            }
          />
          <DocField
            label={t("company:payrolls.payment_date")}
            value={formatHumanDate(formatDate, payment.payDate, true)}
          />
          <DocField
            label={t("company:payrolls.bank_name")}
            value={payment.bankName || "—"}
          />
          <DocField
            label={t("company:payrolls.bank_account")}
            value={payment.accountNumber || "—"}
          />
        </div>
      </div>

      {/* Earnings + Deductions Table */}
      <div className="mx-5 mb-2 border border-gray-200 rounded-sxs overflow-hidden">
        <div className="grid grid-cols-2 divide-x divide-gray-200 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between px-3 py-1.5">
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600">
              {t("company:payrolls.earnings_final_dues")}
            </span>
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600">
              {t("company:payrolls.amount_with_currency", { currency: ccy })}
            </span>
          </div>
          <div className="flex items-center justify-between px-3 py-1.5">
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600">
              {t("company:payrolls.deductions")}
            </span>
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600">
              {t("company:payrolls.amount_with_currency", { currency: ccy })}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-gray-200">
          <div className="px-3 py-2 flex flex-col gap-1">
            {earningsRows.map((e, i) =>
              e ? (
                <div
                  key={i}
                  className="flex justify-between text-[10px] text-gray-700"
                >
                  <span className="truncate max-w-[110px]">
                    {displaySettlementLineName(t, e.name)}
                  </span>
                  <span className="shrink-0 ms-1 tabular-nums">
                    {fmt(Number(e.amount))}
                  </span>
                </div>
              ) : (
                <div
                  key={i}
                  className="text-[10px] text-transparent select-none"
                >
                  .
                </div>
              ),
            )}
          </div>
          <div className="px-3 py-2 flex flex-col gap-1">
            {deductionRows.map((d, i) =>
              d ? (
                <div
                  key={i}
                  className="flex justify-between text-[10px] text-gray-700"
                >
                  <span className="truncate max-w-[110px]">
                    {displaySettlementLineName(t, d.name)}
                  </span>
                  <span className="shrink-0 ms-1 tabular-nums">
                    {d.amount > 0 ? fmt(Number(d.amount)) : "—"}
                  </span>
                </div>
              ) : (
                <div
                  key={i}
                  className="text-[10px] text-transparent select-none"
                >
                  .
                </div>
              ),
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center px-3 py-1.5">
            <span className="text-[10px] font-bold text-gray-700">
              {t("company:payrolls.total_earnings")}
            </span>
            <span className="text-[10px] font-bold text-primary tabular-nums">
              {ccy} {fmt(totalEarnings)}
            </span>
          </div>
          <div className="flex justify-between items-center px-3 py-1.5">
            <span className="text-[10px] font-bold text-gray-700">
              {t("company:payrolls.total_deductions")}
            </span>
            <span className="text-[10px] font-bold text-orange-500 tabular-nums">
              {ccy} {fmt(totalDeductions)}
            </span>
          </div>
        </div>
      </div>

      {/* Statutory Contributions */}
      {statutoryRows.length > 0 && (
        <div className="mx-5 mb-2 border border-gray-200 rounded-sxs overflow-hidden">
          <div
            className="grid bg-gray-50 border-b border-gray-200 px-3 py-1.5"
            style={{ gridTemplateColumns: "34% 26% 20% 20%" }}
          >
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600">
              {t("company:payrolls.contribution")}
            </span>
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600">
              {t("company:payrolls.basis_and_rate")}
            </span>
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600 text-end">
              {t("company:payrolls.employee_with_currency", { currency: ccy })}
            </span>
            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600 text-end">
              {t("company:payrolls.employer_with_currency", { currency: ccy })}
            </span>
          </div>
          <div className="px-3 py-2 flex flex-col gap-1.5">
            {statutoryRows.map((s, i) => (
              <div
                key={i}
                className="grid text-[10px] text-gray-700"
                style={{ gridTemplateColumns: "34% 26% 20% 20%" }}
              >
                <span className="truncate pe-2">{s.label}</span>
                <span className="text-gray-400">
                  {s.basis}
                  {s.rate ? ` · ${s.rate}%` : ""}
                </span>
                <span className="text-end tabular-nums font-semibold text-red-500">
                  {s.employeeAmt > 0 ? fmt(s.employeeAmt) : "—"}
                </span>
                <span className="text-end tabular-nums text-gray-600">
                  {s.employerAmt > 0 ? fmt(s.employerAmt) : "—"}
                </span>
              </div>
            ))}
          </div>
          <div
            className="grid border-t border-gray-200 bg-gray-50 px-3 py-1.5"
            style={{ gridTemplateColumns: "34% 26% 20% 20%" }}
          >
            <span className="text-[10px] font-bold text-gray-700">
              {t("company:payrolls.total_statutory")}
            </span>
            <span />
            <span className="text-end text-[10px] font-bold text-red-500 tabular-nums">
              {fmt(totalStatutoryEmployee)}
            </span>
            <span className="text-end text-[10px] font-bold text-gray-600 tabular-nums">
              {fmt(totalStatutoryEmployer)}
            </span>
          </div>
        </div>
      )}

      {/* Net Salary */}
      <div className="flex items-end justify-between px-5 py-4 border-t border-gray-100 mt-1">
        <div>
          <p className="text-[10px] font-bold text-gray-800 uppercase tracking-wide">
            {t("company:payrolls.net_salary")}
          </p>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {t("company:payrolls.total_take_home_pay_after_all_deductions")}
          </p>
        </div>
        <div className="text-end">
          <p className="text-[9px] text-gray-400 leading-none mb-0.5">{ccy}</p>
          <p className="text-[26px] font-black text-gray-800 leading-none tabular-nums tracking-tight">
            {fmt(netSalary)}
          </p>
        </div>
      </div>

      <div className="h-0.5 bg-primary" />
      <div className="py-2 text-center text-[9px] text-gray-400">
        {t("common:appname")} ·{" "}
        {t("company:employees.generated_on_date", {
          date: formatDate(new Date(), "dd MMM yyyy"),
        })}
      </div>
    </div>
  );
};
